import type { CellEditor, CellEditorProps, TableColumn } from '../../domain';
import { DateEditor, editorLabel, NumberEditor } from './CellEditors';

export interface SelectEditorOption {
    value: string;
    label: string;
}

export interface SelectEditorProps<T> extends CellEditorProps<T> {
    options: SelectEditorOption[];
    parse?: (raw: string) => unknown;
}

export function SelectEditor<T>(props: SelectEditorProps<T>): JSX.Element {
    const { value, ctx, options, parse, onCommit, onCancel } = props;
    const current = value === null || value === undefined ? '' : String(value);

    return (
        <select
            value={current}
            aria-label={editorLabel(ctx)}
            className="ics-table__cell-editor"
            onChange={(e) => {
                onCommit(parse ? parse(e.target.value) : e.target.value);
            }}
            onKeyDown={(e) => {
                if (e.key === 'Escape') onCancel();
            }}
        >
            {options.map((o) => (
                <option key={o.value} value={o.value}>
                    {o.label}
                </option>
            ))}
        </select>
    );
}

function booleanOptions<T>(col: TableColumn<T>): SelectEditorOption[] {
    return (
        col.filterOptions ?? [
            { value: 'true', label: 'Yes' },
            { value: 'false', label: 'No' },
        ]
    );
}

function enumOptions<T>(col: TableColumn<T>): SelectEditorOption[] {
    if (col.filterOptions) return col.filterOptions;
    return Object.entries(col.labels ?? {}).map(([value, label]) => ({
        value,
        label,
    }));
}

function toBoolean(raw: string): boolean {
    return raw === 'true';
}

function booleanEditor<T>(col: TableColumn<T>): CellEditor<T> {
    const options = booleanOptions(col);
    return function BooleanEditor(props: CellEditorProps<T>): JSX.Element {
        return <SelectEditor {...props} options={options} parse={toBoolean} />;
    };
}

function enumEditor<T>(col: TableColumn<T>): CellEditor<T> {
    const options = enumOptions(col);
    return function EnumEditor(props: CellEditorProps<T>): JSX.Element {
        return <SelectEditor {...props} options={options} />;
    };
}

export function resolveBuiltInEditor<T>(
    col: TableColumn<T>,
): CellEditor<T> | undefined {
    if (col.type === 'number') return NumberEditor;
    if (col.type === 'shortDate' || col.type === 'dateTime') return DateEditor;
    if (col.type === 'boolean') return booleanEditor(col);
    if (col.type === 'enum') return enumEditor(col);
    return undefined;
}
